import { useEffect, useMemo, useState } from 'react';
import { SupplierI, SupplierScore } from '../../../../pages/TenderViewPage/types';
import { High, Low, Medium, NoData, Pricing, ScoreBase, ScoreBuilding, ScoreLeading, ScoreStrong } from './icons';
import { annualPriceForSupplier, formatSupplierPriceZAR } from '../../../../utils/supplierPayloadPricing';
import i from '../../../../assets/i.svg';

const GATE_KEYS = ['gate_1', 'gate_2', 'gate_3'] as const;
type GateKey = (typeof GATE_KEYS)[number];

const GATE_LABELS: Record<GateKey, string> = {
    gate_1: 'Gate 1',
    gate_2: 'Gate 2',
    gate_3: 'Gate 3'
};

/** Avg. Trust is only shown when at least this many answers exist for the active gate. */
export const MIN_TRUST_ANSWERS = 5;

/**
 * Answers that back Avg. Trust for the selected gate: Gate 1 = section rows; Gate 2 = evaluator rows
 * if present, else section rows; Gate 3 does not use trust in the stats row.
 */
export function getTrustAnswerCount(
    resolvedGate: GateKey,
    capability: SupplierScore['details']['capability'] | undefined,
    trust: SupplierScore['details']['trust'] | undefined
): number {
    if (resolvedGate === 'gate_1') {
        return capability?.gate_1?.details?.length ?? 0;
    }
    if (resolvedGate === 'gate_2') {
        const evalCount = trust?.details?.length ?? 0;
        if (evalCount > 0) return evalCount;
        return capability?.gate_2?.details?.length ?? 0;
    }
    return MIN_TRUST_ANSWERS;
}

function gateHasData(
    breakdown: { score?: number | null; level?: string | null; details?: { length: number } | null } | null | undefined
): boolean {
    if (breakdown == null) return false;
    const hasDetails = breakdown.details != null && breakdown.details.length > 0;
    const hasScore = breakdown.score !== undefined && breakdown.score !== null;
    const hasLevel = breakdown.level != null && String(breakdown.level).trim() !== '';
    return hasDetails || hasScore || hasLevel;
}

/** When Gate 2 or Gate 3 has data, default tab is the latest gate that has data; otherwise Gate 1. */
function defaultGateForGatesList(gates: readonly GateKey[]): GateKey {
    if (gates.length === 0) return 'gate_1';
    const hasGate2Or3 = gates.some((k) => k === 'gate_2' || k === 'gate_3');
    if (!hasGate2Or3) return 'gate_1';
    return gates[gates.length - 1]!;
}

function capabilityIcon(level: string | undefined) {
    const key = level?.trim().toLowerCase();
    if (key === 'basic') return ScoreBase;
    if (key === 'building') return ScoreBuilding;
    if (key === 'strong') return ScoreStrong;
    if (key === 'leading') return ScoreLeading;
    return NoData;
}

function trustIcon(level: string | undefined) {
    const key = level?.trim().toLowerCase();
    if (key === 'low') return Low;
    if (key === 'medium') return Medium;
    if (key === 'high') return High;
    return NoData;
}

/** Gate 3 numeric score (0–100): higher value → taller trust-style icon (High … NoData). */
function trustIconFromCompetitiveScore(score: number | null | undefined) {
    if (score == null || Number.isNaN(Number(score))) return NoData;
    const s = Math.min(100, Math.max(0, Math.round(Number(score))));
    if (s >= 95) return High;
    if (s >= 85) return Medium;
    if (s >= 70) return Low;
    return NoData;
}

type Variant = 'popover' | 'modal';

export function SupplierScoreBreakdownPanel({
    item,
    variant,
    supplierListPosition
}: {
    item: SupplierI;
    variant: Variant;
    /** 1-based order of this supplier in the tender list (same as the index strip on the card). */
    supplierListPosition?: number;
}) {
    const score = item.score;
    const capability = score?.details?.capability;
    const trust = score?.details?.trust;
    const isPopover = variant === 'popover';

    const gatesWithData = useMemo(
        () =>
            GATE_KEYS.filter((k) => {
                if (k === 'gate_2' && trust?.details != null && trust.details.length > 0) return true;
                return gateHasData(capability?.[k]);
            }),
        [capability, trust]
    );

    const [pickedGate, setPickedGate] = useState<GateKey | null>(null);

    useEffect(() => {
        setPickedGate(null);
    }, [item.id]);

    const resolvedGate: GateKey = useMemo(() => {
        if (gatesWithData.length === 0) return 'gate_1';
        const preferred = defaultGateForGatesList(gatesWithData);
        if (pickedGate != null && gatesWithData.includes(pickedGate)) return pickedGate;
        return preferred;
    }, [gatesWithData, pickedGate]);

    const gateBreakdown = useMemo(() => {
        if (gatesWithData.length === 0) return undefined;
        if (resolvedGate === 'gate_2') {
            const capG2 = capability?.gate_2;
            if (capG2 && gateHasData(capG2)) return capG2;
            if (trust?.details != null && trust.details.length > 0) return trust;
            return capG2;
        }
        return capability?.[resolvedGate];
    }, [gatesWithData, resolvedGate, capability, trust]);

    const trustAnswerCount = useMemo(
        () => getTrustAnswerCount(resolvedGate, capability, trust),
        [resolvedGate, capability, trust]
    );
    const showTrustAggregate = resolvedGate === 'gate_3' || trustAnswerCount >= MIN_TRUST_ANSWERS;

    const trustIconEl = showTrustAggregate ? trustIcon(trust?.level) : NoData;
    const trustLabel = showTrustAggregate ? trust?.level?.trim() || '—' : 'No data';

    const annualPrice = useMemo(() => annualPriceForSupplier(item), [item]);

    const statsCapabilityIcon = capabilityIcon(gateBreakdown?.level ?? score?.avg?.capability_level);
    const statsCapabilityPercent =
        gateBreakdown?.score !== undefined && gateBreakdown?.score !== null
            ? `${gateBreakdown.score}%`
            : score?.avg?.capability_score !== undefined && score?.avg?.capability_score !== null
            ? `${score.avg.capability_score}%`
            : '';
    const statsCapabilityLevel = gateBreakdown?.level?.trim() || score?.avg?.capability_level?.trim() || '—';

    const leftStatsPercent =
        resolvedGate === 'gate_3'
            ? gateBreakdown?.score !== undefined && gateBreakdown?.score !== null
                ? `${gateBreakdown.score}%`
                : ''
            : statsCapabilityPercent;
    const leftStatsLevel = resolvedGate === 'gate_3' ? gateBreakdown?.level?.trim() || '—' : statsCapabilityLevel;
    const leftStatsCaption = `${GATE_LABELS[resolvedGate]} Score`;

    const gate3NumericScore =
        resolvedGate === 'gate_3' && gateBreakdown?.score !== undefined && gateBreakdown?.score !== null
            ? Number(gateBreakdown.score)
            : null;
    const leftStatsIcon =
        resolvedGate === 'gate_3' ? trustIconFromCompetitiveScore(gate3NumericScore) : statsCapabilityIcon;
    const leftStatsIconClassName =
        resolvedGate === 'gate_3'
            ? 'score-breakdown-stats__icon score-breakdown-stats__icon--trust'
            : 'score-breakdown-stats__icon score-breakdown-stats__icon--capability';

    const avgCapabilityLevelRaw = score?.avg?.capability_level;
    const avgCapabilityKey = avgCapabilityLevelRaw?.trim().toLowerCase();
    const modalHeaderCapabilityIcon = capabilityIcon(avgCapabilityLevelRaw);
    const modalHeaderCapabilityScoreNum = score?.avg?.capability_score;
    const modalHeaderCapabilitySpan =
        avgCapabilityKey === 'basic' ||
        avgCapabilityKey === 'building' ||
        avgCapabilityKey === 'strong' ||
        avgCapabilityKey === 'leading'
            ? avgCapabilityLevelRaw!.trim()
            : avgCapabilityLevelRaw?.trim() || 'No data';

    if (!score) {
        const msg = 'Score breakdown is not available yet.';
        if (isPopover) {
            return <div className="supplier-card-score-popover__empty">{msg}</div>;
        }
        return (
            <div className="score-block">
                <div className="score-card" style={{ padding: 16, color: '#808cb2', fontSize: 14 }}>
                    {msg}
                </div>
            </div>
        );
    }

    const statsBlock = (
        <div
            className={
                isPopover
                    ? 'stats score-breakdown-stats supplier-card-score-popover__stats'
                    : 'stats score-breakdown-stats'
            }
        >
            <div className="stat">
                <div className="stat__value score-breakdown-stats__row">
                    <span className={leftStatsIconClassName}>{leftStatsIcon}</span>
                    {leftStatsPercent ? (
                        <span className="score-breakdown-stats__percent">{leftStatsPercent}</span>
                    ) : null}
                    <span className="score-breakdown-stats__level">{leftStatsLevel}</span>
                </div>
                <div className="stat__label score-breakdown-stats__caption">{leftStatsCaption}</div>
            </div>

            <div className="divider score-breakdown-stats__divider" />

            <div className="stat">
                <div className="stat__value score-breakdown-stats__row">
                    {resolvedGate === 'gate_3' ? (
                        <>
                            <span className="score-breakdown-stats__icon score-breakdown-stats__icon--pricing">
                                {Pricing}
                            </span>
                            {annualPrice != null ? (
                                <>
                                    <span className="score-breakdown-stats__pricing-amount">
                                        {formatSupplierPriceZAR(annualPrice)}
                                    </span>
                                    <span className="score-breakdown-stats__pricing-period">/year</span>
                                </>
                            ) : (
                                <span className="score-breakdown-stats__pricing-empty">—</span>
                            )}
                        </>
                    ) : (
                        <>
                            <span className="score-breakdown-stats__icon score-breakdown-stats__icon--trust">
                                {trustIconEl}
                            </span>
                            <span className="score-breakdown-stats__trust">{trustLabel}</span>
                        </>
                    )}
                </div>
                <div className="stat__label score-breakdown-stats__caption">
                    {resolvedGate === 'gate_3' ? 'Avg. Price' : 'Avg. Trust'}
                </div>
            </div>
        </div>
    );

    const gatesBlock =
        gatesWithData.length > 0 ? (
            <div className="gates">
                {GATE_KEYS.map((key) => {
                    const has = gatesWithData.includes(key);
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`gate ${resolvedGate === key && has ? 'active' : ''} ${
                                !has ? 'gate--disabled' : ''
                            }`}
                            disabled={!has}
                            onClick={() => has && setPickedGate(key)}
                        >
                            {GATE_LABELS[key]}
                        </button>
                    );
                })}
            </div>
        ) : null;

    const ratingBlock =
        supplierListPosition != null && supplierListPosition > 0 ? (
            <div className="rating-position">
                <span className="rating-position__label">Rating Position</span>
                <span className="rating-position__value">#{supplierListPosition}</span>
            </div>
        ) : null;

    const showGate2EvaluatorTable =
        resolvedGate === 'gate_2' && trust?.details != null && trust.details.length > 0;

    const capabilityBreakdownForGate = capability?.[resolvedGate];

    const evaluatorTableRows =
        showGate2EvaluatorTable && trust?.details ? (
            trust.details.map((d, idx) => (
                <div className="score-table__table-row" key={`${d.user_name?.trim() || 'evaluator'}-${idx}`}>
                    <span>{d.user_name?.trim() || '—'}</span>
                    <span>{d.level?.trim() || '—'}</span>
                    <span className="right">{d.score !== undefined && d.score !== null ? `${d.score}%` : '—'}</span>
                </div>
            ))
        ) : null;

    const sectionTableRows =
        capabilityBreakdownForGate?.details && capabilityBreakdownForGate.details.length > 0 ? (
            capabilityBreakdownForGate.details.map((d, idx) => (
                <div className="score-table__table-row" key={`${d.section_title ?? 'section'}-${idx}`}>
                    <span>{d.section_title?.trim() || '—'}</span>
                    <span>{d.level?.trim() || '—'}</span>
                    <span className="right">{d.score !== undefined && d.score !== null ? `${d.score}%` : '—'}</span>
                </div>
            ))
        ) : null;

    const sectionTableEmpty = (
        <div className="supplier-card-score-popover__empty-rows">No section breakdown for this gate.</div>
    );

    const sectionTableBody = sectionTableRows ?? sectionTableEmpty;

    const sectionTableHeader = (
        <div className="score-table__table-header">
            <span>Section</span>
            <span>Classification</span>
            <span className="right">Score</span>
        </div>
    );

    const sectionTableMarkup = isPopover ? (
        <div className="score-table supplier-card-score-popover__table">
            {sectionTableHeader}
            <div className="supplier-card-score-popover__table-scroll">{sectionTableBody}</div>
        </div>
    ) : (
        <div className="score-table">
            {sectionTableHeader}
            {sectionTableBody}
        </div>
    );

    const evaluatorTable =
        showGate2EvaluatorTable && evaluatorTableRows ? (
            isPopover ? (
                <div className="score-table score-table--gate2-evaluators supplier-card-score-popover__table">
                    <div className="score-table__table-header">
                        <span>Evaluator</span>
                        <span>Classification</span>
                        <span className="right">Score</span>
                    </div>
                    <div className="supplier-card-score-popover__table-scroll">{evaluatorTableRows}</div>
                </div>
            ) : (
                <div className="score-table score-table--gate2-evaluators">
                    <div className="score-table__table-header">
                        <span>Evaluator</span>
                        <span>Classification</span>
                        <span className="right">Score</span>
                    </div>
                    {evaluatorTableRows}
                </div>
            )
        ) : null;

    const tableBlock =
        resolvedGate === 'gate_3' ? (
            <div className="note" style={{ margin: '8px 0' }}>
                <img src={i} />
                <div>
                    For itemised pricing, please download the pricing breakdown. For assumptions made in the pricing,
                    please download the bid conditions.
                </div>
            </div>
        ) : resolvedGate === 'gate_2' ? (
            <div className="score-breakdown-tables">
                {sectionTableMarkup}
                {evaluatorTable}
            </div>
        ) : (
            sectionTableMarkup
        );

    const core = (
        <>
            {gatesBlock}
            {ratingBlock}
            {statsBlock}
            {tableBlock}
        </>
    );

    if (isPopover) {
        return <div className="supplier-card-score-popover__inner">{core}</div>;
    }

    return (
        <div className="score-block">
            <div className="score-header">
                <div className="score-label">Average Score:</div>
                <div className="stat score-header__stat">
                    <div className="stat__value">
                        {modalHeaderCapabilityIcon}
                        {modalHeaderCapabilityScoreNum !== undefined && modalHeaderCapabilityScoreNum !== null
                            ? `${modalHeaderCapabilityScoreNum}% `
                            : ''}
                        <span>{modalHeaderCapabilitySpan}</span>
                    </div>
                </div>
            </div>
            <div className="score-card">{core}</div>
        </div>
    );
}
